import React, { useCallback, useEffect, useMemo } from 'react';
import {
  useGetAgentLimitForm,
  useGetResolutionLimitOptions,
  useUpdateAgentLimit,
} from '../apis';
import { useForm } from 'react-hook-form';
import { Star } from '../../../UiKit/Star/Star';
import { Button } from '@grofers/react';
import Spinner from 'sushiweb/Spinner/Spinner';
import { grey } from 'sushiweb/tokens/color';
import { toast } from 'react-hot-toast';
import { EmptyState } from '../../../components/commons';
import { InvalidSearchImg } from '../../../images/images';
import { Dropdown } from '../../../UiKit';

const FIELDS = {
  agentEmail: 'agent_email',
  resolutionLimit: 'resolution_limit',
  validity: 'validity',
};

const FIELDTYPES = {
  text: 'text',
  select: 'select',
};

const UpdateAgentLimit = () => {
  const form = useForm({
    defaultValues: {
      [FIELDS.agentEmail]: '',
      [FIELDS.resolutionLimit]: '',
      [FIELDS.validity]: '',
    },
  });
  const { handleSubmit, formState, resetField, getValues } = form;
  const { isValid } = formState;

  const {
    data: agentLimitForm,
    isLoading: formLoading,
    isError: formError,
    error: formErrorData,
  } = useGetAgentLimitForm();

  const {
    data: resolutionLimitOptions,
    refetch: fetchResolutionLimitOptions,
    error: resolutionLimitErrorData,
  } = useGetResolutionLimitOptions(getValues(FIELDS.agentEmail));

  const { mutate: updateAgentLimit, isLoading: updateAgentLimitLoading } =
    useUpdateAgentLimit({
      meta: {
        hideAlert: true,
      },
      onSuccess: (data) => {
        if (data.success) {
          agentLimitForm.forEach((fieldName) => {
            resetField(fieldName.id);
          });
          toast.success(data.message);
        }
      },
    });

  const resolutionOptions = useMemo(() => {
    if (resolutionLimitOptions?.resolution_limit) {
      return resolutionLimitOptions.resolution_limit;
    } else if (resolutionLimitOptions) {
      toast.error(resolutionLimitOptions.message);
      return { message: resolutionLimitOptions.message };
    }
    return {};
  }, [resolutionLimitOptions]);

  if (formError || formLoading) {
    return (
      <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-h-max tw-w-full tw-flex tw-flex-col tw-items-center'>
        {formError ? (
          <EmptyState
            img={InvalidSearchImg}
            heading={
              formErrorData?.message || 'Oops !! Something went wrong :('
            }
          />
        ) : (
          <Spinner size='small' color={`${grey.z300}`} />
        )}
      </div>
    );
  }

  const renderField = (field, index) => {
    return (
      <FieldComp
        field={field}
        key={field.id}
        form={form}
        index={index}
        resolutionOptions={resolutionOptions}
        fetchResolutionLimitOptions={fetchResolutionLimitOptions}
      />
    );
  };

  return (
    <form
      className='tw-bg-white tw-p-8 tw-rounded-2xl tw-h-max tw-w-full'
      onSubmit={handleSubmit(updateAgentLimit)}
    >
      {agentLimitForm &&
        agentLimitForm.map((field, index) => renderField(field, index))}
      {resolutionLimitErrorData?.message && (
        <p>{resolutionLimitErrorData?.message}</p>
      )}
      <div className='tw-mt-10'>
        <Button
          variant={'fill'}
          size={'medium'}
          className={`tw-h-14 tw-w-max tw-p-4 ${
            !isValid &&
            'tw-bg-grey-300 hover:tw-bg-grey-300 active:tw-bg-grey-300 tw-cursor-not-allowed'
          }`}
        >
          {updateAgentLimitLoading ? (
            <Spinner size='small' color={`${grey.z300}`} />
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </form>
  );
};

const FieldComp = ({
  field,
  form,
  index,
  fetchResolutionLimitOptions,
  resolutionOptions,
}) => {
  const { id, type, label, required, options, disabled, placeholder } = field;
  const { register, setValue, trigger, getValues, formState } = form;
  const { errors } = formState;

  const saveOptions = useCallback(
    (key, value) => {
      setValue(key, key === FIELDS.agentEmail ? value : Number(value));
      trigger(key).then((res) => {
        res &&
          key === FIELDS.agentEmail &&
          setTimeout(() => {
            let timeoutId;
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
              const agentEmail = getValues(key);
              fetchResolutionLimitOptions(agentEmail);
            }, 500);
          });
      });
    },
    [setValue, trigger, fetchResolutionLimitOptions, getValues],
  );

  const optionsToRender = useMemo(() => {
    if (type === FIELDTYPES.select && options?.length) {
      return options.reduce((acc, option) => {
        return {
          ...acc,
          [option.id]: option.name,
        };
      }, {});
    } else if (type === FIELDTYPES.select && resolutionOptions?.length) {
      return resolutionOptions.reduce((acc, option) => {
        return {
          ...acc,
          [option.id]: option.name,
        };
      }, {});
    }
    return {};
  }, [options, type, resolutionOptions]);

  useEffect(() => {
    register(id, {
      required: {
        value: required,
        message: `${label} is required`,
      },
      pattern:
        type === FIELDTYPES.text
          ? {
              value: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{3,}\b/,
              message: `Invalid ${label}`,
            }
          : null,
    });
  }, [id, label, required, type, register]);

  return (
    <div
      className={`${
        index === 0
          ? 'tw-block tw-w-full tw-mb-8'
          : 'tw-inline-block tw-w-[48.5%]'
      } ${index === 1 ? 'tw-mr-12' : ''} `}
    >
      <div className={`tw-flex-1 tw-flex tw-flex-col tw-gap-4`}>
        <div className={`tw-text-[#1F1F1F] tw-font-bold tw-text-500 tw-block`}>
          {label}:{required && <Star icon={'*'} />}
        </div>
        {type === 'text' ? (
          <>
            <input
              data-dd-privacy='mask'
              placeholder={placeholder}
              type={type}
              value={getValues(id)}
              onChange={(e) => saveOptions(id, e.target.value)}
              name={id}
              disabled={disabled}
              autoComplete='off'
              className='inputBox medium'
            />
            {errors[id] && <p className='errorText'>{errors[id]?.message}</p>}
            {resolutionOptions?.message && (
              <p className='tw-text-[#BF2938]'>{resolutionOptions.message}</p>
            )}
          </>
        ) : (
          <div
            css={`
              & div {
                width: 100%;
                display: flex;
              }
            `}
            className={`tw-w-full tw-flex tw-flex-row tw-gap-6 ${
              (
                id === FIELDS.resolutionLimit
                  ? !Object.keys(optionsToRender).length
                  : disabled
              )
                ? 'tw-cursor-not-allowed'
                : 'tw-cursor-pointer'
            }`}
          >
            <label
              css={`
                & div {
                  width: 100%;
                  display: flex;
                }
              `}
              className={`tw-w-full tw-flex tw-flex-row tw-gap-6 ${
                (
                  id === FIELDS.resolutionLimit
                    ? !Object.keys(optionsToRender).length
                    : disabled
                )
                  ? 'tw-pointer-events-none'
                  : 'tw-pointer-events-auto'
              }`}
            >
              <Dropdown
                placeholder={placeholder}
                disabled={
                  id === FIELDS.resolutionLimit
                    ? !Object.keys(optionsToRender).length
                    : disabled
                }
                onSelect={(value) => saveOptions(id, value)}
                dropdownVal={getValues(id)}
                dropDownValues={optionsToRender}
                error={errors[id] || false}
                errorText={errors[id]?.message}
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateAgentLimit;
